import { useNavigationState } from '../navigation/NavigationProvider';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { DialogContent } from '../../components/ui/DialogContent';
import { DialogActions } from '../../components/ui/DialogActions';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import {
  deriveCollectionInvitationExchangeToken,
  parseAcceptCollectionInvitationResult,
  parseDeclineCollectionInvitationResult,
  parseExchangeCollectionInvitationResult,
  requireOperationId,
} from '@qkindex/contracts';
import { createOperationId } from '../../domain/operationId';
import {
  acceptCollectionInvitation,
  declineCollectionInvitation,
  exchangeCollectionInvitation,
} from '../../firebase/functions';
import { collectionPeopleErrorMessage } from './collectionPeopleService';

const STORAGE_KEY = 'qkindex.collectionInvitationExchange.v1';
const INVITATION_PARAMETER = 'invite';

interface StoredInvitationExchange {
  token: string;
  acceptOperationId?: string;
  declineOperationId?: string;
}

const readStoredExchange = (): StoredInvitationExchange | undefined => {
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return undefined;
  try {
    const value = JSON.parse(raw) as Partial<StoredInvitationExchange>;
    if (
      typeof value.token !== 'string' ||
      !/^[A-Za-z0-9_-]{32,512}$/u.test(value.token)
    ) {
      throw new Error('invalid token');
    }
    const acceptOperationId =
      value.acceptOperationId === undefined
        ? undefined
        : requireOperationId(value.acceptOperationId);
    const declineOperationId =
      value.declineOperationId === undefined
        ? undefined
        : requireOperationId(value.declineOperationId);
    return {
      token: value.token,
      ...(acceptOperationId ? { acceptOperationId } : {}),
      ...(declineOperationId ? { declineOperationId } : {}),
    };
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return undefined;
  }
};

const writeStoredExchange = (value: StoredInvitationExchange): void => {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
};

const suppressSensitiveTelemetry = (suppressed: boolean): void => {
  (
    window as unknown as Record<string, unknown>
  ).__FOUND_IT_INVITATION_SANITIZING__ = suppressed;
};

const ensureNoReferrer = (): void => {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="referrer"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'referrer';
    document.head.appendChild(meta);
  }
  meta.content = 'no-referrer';
};

const sanitizeInvitationUrl = (
  navigate: NavigateFunction,
  state: unknown,
): void => {
  const url = new URL(window.location.href);
  const fragment = new URLSearchParams(url.hash.slice(1));
  fragment.delete(INVITATION_PARAMETER);
  url.hash = fragment.toString();
  navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true, state });
};

const invitationTokenFromUrl = (url: URL): string | null =>
  new URLSearchParams(url.hash.slice(1)).get(INVITATION_PARAMETER);

const contractCode = (error: unknown, depth = 0): string | undefined => {
  if (!error || typeof error !== 'object' || depth > 3) return undefined;
  const value = error as {
    code?: unknown;
    details?: { code?: unknown };
    cause?: unknown;
  };
  if (typeof value.details?.code === 'string') return value.details.code;
  return (
    contractCode(value.cause, depth + 1) ||
    (typeof value.code === 'string' ? value.code : undefined)
  );
};

export const useInvitationLanding = ({
  authenticated,
  onAccepted,
}: {
  authenticated: boolean;
  onAccepted(): Promise<void>;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [panel, setPanel] = useNavigationState<
    'decision' | 'unavailable' | null
  >('invitation', null);
  const initialStored = useRef<StoredInvitationExchange | undefined>(
    invitationTokenFromUrl(new URL(window.location.href))
      ? undefined
      : readStoredExchange(),
  );
  const [exchangeToken, setExchangeToken] = useState<string | undefined>(
    initialStored.current?.token,
  );
  const [exchangeError, setExchangeError] = useState<string>();
  const [busy, setBusy] = useState<'accept' | 'decline'>();
  const [decisionError, setDecisionError] = useState<string>();
  const operationIds = useRef<{ accept?: string; decline?: string }>({
    accept: initialStored.current?.acceptOperationId,
    decline: initialStored.current?.declineOperationId,
  });

  useEffect(() => {
    const handleCheckToken = () => {
      const url = new URL(window.location.href);
      const rawToken = invitationTokenFromUrl(url);
      if (!rawToken) return;
      window.sessionStorage.removeItem(STORAGE_KEY);
      operationIds.current = {};
      setExchangeToken(undefined);
      ensureNoReferrer();
      suppressSensitiveTelemetry(true);
      const pendingExchange = exchangeCollectionInvitation({ token: rawToken });
      sanitizeInvitationUrl(navigate, location.state);
      suppressSensitiveTelemetry(false);
      void deriveCollectionInvitationExchangeToken(rawToken)
        .then(async (derivedToken) => {
          writeStoredExchange({ token: derivedToken });
          try {
            const result = parseExchangeCollectionInvitationResult(
              (await pendingExchange).data,
            );
            if (result.exchangeToken !== derivedToken)
              throw new Error('Invitation exchange mismatch.');
            setExchangeToken(derivedToken);
            setExchangeError(undefined);
          } catch (error) {
            const code = contractCode(error);
            if (
              code?.includes('invalid-request') ||
              code?.includes('collection-unavailable')
            ) {
              window.sessionStorage.removeItem(STORAGE_KEY);
              setExchangeToken(undefined);
              setExchangeError(
                'This invitation is unavailable or has expired.',
              );
            } else {
              // The POST may have committed even when its response was lost. The one-way
              // capability and persisted operation identity make the decision retry-safe.
              setExchangeToken(derivedToken);
            }
          }
        })
        .catch(() => {
          window.sessionStorage.removeItem(STORAGE_KEY);
          setExchangeToken(undefined);
          setExchangeError('This invitation is unavailable or has expired.');
          void pendingExchange.catch(() => undefined);
        });
    };

    handleCheckToken();
    window.addEventListener('hashchange', handleCheckToken);
    return () => {
      window.removeEventListener('hashchange', handleCheckToken);
    };
  }, [navigate, location.state]);

  const clear = () => {
    window.sessionStorage.removeItem(STORAGE_KEY);
    operationIds.current = {};
    setExchangeToken(undefined);
    setDecisionError(undefined);
  };

  useEffect(() => {
    if (exchangeToken) setPanel('decision');
    else if (exchangeError) setPanel('unavailable');
  }, [exchangeToken, exchangeError, setPanel]);
  const { requestClose, closeAfterSave } = useUnsavedChangesGuard({
    scopeKey: 'invitation',
    open: authenticated && panel === 'decision' && Boolean(exchangeToken),
    dirty: false,
    pending: Boolean(busy),
    onDiscard: () => {},
    onClose: () => {
      clear();
      setPanel(null);
    },
  });
  const dismiss = () => {
    void requestClose();
  };
  const dismissError = () => {
    setExchangeError(undefined);
    setPanel(null);
  };

  const decide = async (decision: 'accept' | 'decline') => {
    if (!exchangeToken || busy) return;
    setBusy(decision);
    setDecisionError(undefined);
    try {
      operationIds.current[decision] ||= createOperationId();
      writeStoredExchange({
        token: exchangeToken,
        ...(operationIds.current.accept
          ? { acceptOperationId: operationIds.current.accept }
          : {}),
        ...(operationIds.current.decline
          ? { declineOperationId: operationIds.current.decline }
          : {}),
      });
      if (decision === 'accept') {
        parseAcceptCollectionInvitationResult(
          (
            await acceptCollectionInvitation({
              operationId: operationIds.current.accept!,
              exchangeToken,
            })
          ).data,
        );
        closeAfterSave();
        await onAccepted();
      } else {
        parseDeclineCollectionInvitationResult(
          (
            await declineCollectionInvitation({
              operationId: operationIds.current.decline!,
              exchangeToken,
            })
          ).data,
        );
        closeAfterSave();
      }
    } catch (error) {
      const code = contractCode(error);
      if (
        code?.includes('collection-unavailable') ||
        code?.includes('invalid-request')
      ) {
        clear();
        setExchangeError('This invitation is unavailable or has expired.');
      } else {
        setDecisionError(collectionPeopleErrorMessage(error));
      }
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <>
      <ResponsiveDialog
        open={authenticated && panel === 'decision' && Boolean(exchangeToken)}
        aria-labelledby="invitation-dialog-title"
        onClose={dismiss}
      >
        <DialogHeader
          id="invitation-dialog-title"
          title="Collection invitation"
          onClose={dismiss}
          closeDisabled={Boolean(busy)}
        />
        <DialogContent>
          Accept to add this collection to your qkIndex account. The invitation
          must match your verified email.
          {decisionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {decisionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={Boolean(busy)}
            onClick={() => {
              void decide('decline');
            }}
          >
            {busy === 'decline' ? 'Declining…' : 'Decline'}
          </Button>
          <Button
            variant="contained"
            disabled={Boolean(busy)}
            onClick={() => {
              void decide('accept');
            }}
          >
            {busy === 'accept' ? 'Accepting…' : 'Accept invitation'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
      <ResponsiveDialog
        aria-labelledby="invitation-unavailable-title"
        open={
          authenticated && panel === 'unavailable' && Boolean(exchangeError)
        }
        onClose={dismissError}
      >
        <DialogHeader
          id="invitation-unavailable-title"
          title="Invitation unavailable"
          onClose={dismissError}
        />
        <DialogContent>{exchangeError}</DialogContent>
        <DialogActions>
          <Button onClick={dismissError}>Close</Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
};
